import type { FaceMap } from '@gymcloud/faces'
import { normalizeExerciseName } from '@gymcloud/mixedscript'

import type { LoggedSet } from '~/data/types'
import { canHideFacesHere, FacesHeldBack } from '~/lib/face-maps'

/**
 * A set's video as a copy to keep — fetched whole, and with the faces of the [hidden] people
 * blurred into it off [map], what the faces step settled (`/specs/features/faces.spec.md`). With
 * nobody hidden it is the video as filmed. It is fetched rather than linked: the media sits on
 * the API's own origin, and a `download` attribute across origins is ignored.
 */
export async function makeSetCopy(
  url: string,
  hidden: ReadonlySet<string>,
  map: FaceMap | null,
  {
    onProgress,
    signal,
    original,
  }: {
    onProgress?: (progress: number) => void
    signal?: AbortSignal
    /** The video as filmed, where it is already held — fetched otherwise. */
    original?: (url: string) => Promise<Blob>
  } = {},
): Promise<Blob> {
  const blurs = hidden.size > 0
  if (blurs && !canHideFacesHere()) {
    throw new FacesHeldBack('cannot-here')
  }
  if (blurs && map === null) {
    throw new FacesHeldBack('not-found')
  }
  const filmed = original ? await original(url) : await fetchVideo(url, signal)
  if (!blurs || map === null) {
    return filmed
  }
  const { hideFacesIn } = await import('~/lib/hide-faces')
  return hideFacesIn(filmed, map, hidden, {
    ...(onProgress ? { onProgress } : {}),
    ...(signal ? { signal } : {}),
  })
}

/** A set's video as filmed, fetched whole. */
export async function fetchVideo(url: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(url, signal ? { signal } : {})
  if (!response.ok) {
    throw new Error(String(response.status))
  }
  return response.blob()
}

/**
 * Hands a set's copy to the browser to save. One set or twenty go the same way, so the viewer
 * and a selection cannot end up saving under different names.
 */
export function saveSetCopy(copy: Blob, set: LoggedSet): void {
  saveBlob(copy, `${normalizeExerciseName(set.exerciseName)}-${set.id}.mp4`)
}

/** Hands bytes already fetched to the browser to save under [name]. */
export function saveBlob(blob: Blob, name: string): void {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = name
  link.click()
  URL.revokeObjectURL(href)
}
